/**
 * Perf evidence: a one-line summary above the A/B table the Performance page uses.
 *
 * @module features/proofs/components/PerfEvidenceBody
 */
import React from 'react';
import { DataTable, Text } from '@chatsdet/ui';
import { sortRows } from '../../perf/perfPresentation';
import { ROW_COLUMNS } from '../../perf/perfRowColumns';
import type { ProofEvidence } from '../types';
import { sha7 } from '../evidenceKinds';

type Perf = Extract<ProofEvidence, { kind: 'perf' }>;

/** The row's one-line form: the shas and the verdict. */
export const perfRowSummary = (item: Perf): string =>
  [item.baseSha && item.headSha ? `${sha7(item.baseSha)} vs ${sha7(item.headSha)}` : null, item.verdict?.replaceAll('_', ' ')]
    .filter((part): part is string => Boolean(part))
    .join(', ');

/** What the row header does not already say: rounds and environment. */
const perfDetail = (item: Perf): string =>
  [item.rounds !== null ? `${item.rounds} rounds` : null, item.environment]
    .filter((part): part is string => Boolean(part))
    .join(', ');

export const PerfEvidenceBody: React.FC<{ item: Perf }> = ({ item }) => (
  <div className="space-y-3">
    {perfDetail(item) && <Text color="muted">{perfDetail(item)}</Text>}
    {item.rows ? (
      <DataTable columns={ROW_COLUMNS} data={sortRows(item.rows)} aria-label="Perf A/B table" getRowId={(row) => row.id} />
    ) : (
      <Text color="muted">This run is no longer available.</Text>
    )}
  </div>
);
